// Internal imports
import { ROLE_TYPE } from '~/app/constants.js';
// Public Routes
import NotFound from '~/app/Pages/NotFound/NotFound.jsx';
import Login from '~/app/Pages/Login/Login.jsx';
import SSOAccountPicker from '~/app/Pages/SSOAccountPicker/AccountPicker.jsx';
import SSONoAccount from '~/app/Pages/SSOAccountPicker/NoAccount.jsx';
import SSOHandoff from '~/app/Pages/SSOAccountPicker/Handoff.jsx';
import ForgotUsername from '~/app/Pages/ForgotUsername/ForgotUsername.jsx';
import ForgotPassword from '~/app/Pages/ForgotPassword/ForgotPassword.jsx';
import OverviewV2 from '~/app/Components/Dashboard/OverviewV2/OverviewV2.jsx';
import ActivateAcc from '~/app/Components/NewAccount/ActivateAcc.jsx';
import ShowHeaders from '~/app/Components/Common/ShowHeaders.jsx';
import RemoteSignatureAuth from '~/app/Components/RemoteSign/RemoteSignatureAuth.jsx';
import RemoteRequestDocs from '~/app/Components/RemoteRequestDocs/RemoteRequestDocs.jsx';
import PreparerRemoteSign from '~/app/Components/RemoteSign/PreparerRemoteSign.jsx';
import QRModalMobileDone from '~/app/Components/NewAccount/QRModalMobileDone.jsx';
import StatePackages from '../Pages/Packages/StatePackages.jsx';
// Private Routes
import MFAMethod from '~/app/Pages/MFAMethod/MFAMethod.jsx';
import MFACode from '~/app/Pages/MFACode/MFACode.jsx';
import TermsAndConditions from '~/app/Pages/TermsAndConditions/TermsAndConditions.jsx';
import IRSMessage from '~/app/Pages/IRSMessage/IRSMessage.jsx';
import FormViewer from '~/app/Pages/Returns/FormViewerWrapper.jsx';
import Settings from '~/app/Pages/Setup/Settings.jsx';
import FormViewerLite from '~/app/Pages/RefundCalculator/FormViewerLite.jsx';
import {
  LoadablePreparers,
  LoadableReporting,
  LoadableReportBuilder,
  LoadableTaxReturns,
  LoadableMessages,
  LoadableAppointments,
  LoadablePayments,
  LoadableReadyToPrint,
  LoadableCheckRegister,
  LoadableAccounts,
  LoadableOffices,
  LoadableServer,
  LoadableUserLookup,
  LoadableMoveReturn,
  LoadableTechLogins,
  LoadableUtilities,
  LoadableSSOMapping,
} from '~/app/Router/helper/routesHelper.js';
import ReturnProfile from '~/app/Pages/ReturnProfile/ReturnProfile.jsx';
// TO DO: Fully remove GenericOverview once OverviewV2 is fully implemented
// import GenericOverview from '#/Dashboard/Overview/GenericOverview/GenericOverview.jsx';
import NewAccount from '~/app/Pages/NewAccount/NewAccount.jsx';
import WizardMode from '~/app/Pages/WizardMode/WizardMode.jsx';
import AppBody from '~/app/AppBody.jsx';

// Routes accessible without logging in / being authenticated
export const publicRoutes = [
  {
    path: '/',
    exact: true,
    Component: Login,
    pageName: 'Landing',
  },
  // Keep SSO public routes aligned with the login-button feature flag so direct
  // navigation to callback/picker/handoff endpoints is unavailable when disabled.
  ...(SSO_LOGIN_ENABLED === 'true'
    ? [
        {
          path: '/sso-account-picker',
          exact: true,
          Component: SSOAccountPicker,
          pageName: 'SSO Account Picker',
        },
        {
          path: '/sso-no-account',
          exact: true,
          Component: SSONoAccount,
          pageName: 'SSO No Account',
        },
        {
          path: '/sso-handoff',
          exact: true,
          Component: SSOHandoff,
          pageName: 'SSO Handoff',
        },
      ]
    : []),
  {
    path: '/forgot-username',
    exact: true,
    Component: ForgotUsername,
    pageName: 'Forgot Username',
  },
  {
    path: '/forgot-password',
    exact: true,
    Component: ForgotPassword,
    pageName: 'Forgot Password',
  },
  {
    path: '/activate/:code',
    Component: ActivateAcc,
    pageName: 'Activate Account',
  },
  {
    path: '/showheaders',
    Component: ShowHeaders,
    pageName: 'Show Headers',
  },
  {
    path: '/remotesign/:code',
    Component: RemoteSignatureAuth,
    pageName: 'Remote Signature Authentication',
  },
  {
    path: '/remotedocuments/:code',
    Component: RemoteRequestDocs,
    pageName: 'Remote Requested Documents',
  },
  {
    path: '/capture-prep-signature/:code',
    Component: PreparerRemoteSign,
    pageName: 'Preparer Remote Signature Authentication',
  },
  {
    path: '/mobactivationdone',
    Component: QRModalMobileDone,
    pageName: 'QR Mobile Activation',
  },
];

// Routes only accessible once logged in / authenticated
export const privateRoutes = [
  {
    path: '/mfa-method',
    exact: true,
    Component: MFAMethod,
    pageName: 'MFA Method',
  },
  {
    path: '/mfa-code',
    exact: true,
    Component: MFACode,
    pageName: 'MFA Code',
  },
  {
    path: '/terms-and-conditions',
    exact: true,
    Component: TermsAndConditions,
    pageName: 'Terms and Conditions',
  },
  {
    path: '/irs-message',
    exact: true,
    Component: IRSMessage,
    pageName: 'IRS Message',
  },
  {
    path: '/tax-return',
    exact: true,
    Component: FormViewer,
    pageName: 'Tax Return',
  },
  {
    path: '/refund-calculator',
    exact: true,
    Component: FormViewerLite,
    pageName: 'Refund Calculator',
  },
  {
    path: '/account-settings',
    exact: true,
    Component: Settings,
    pageName: 'Account Settings',
  },
  {
    path: '/create-new-account',
    exact: true,
    Component: NewAccount,
    pageName: 'Create New Account',
  },
  {
    path: '/wizard-mode',
    exact: true,
    Component: WizardMode,
    pageName: 'Wizard Mode',
  },
  // Remove the navigation key once the routes below are moved to their own dedicated directory in Pages.

  {
    path: '/reporting/quick-reports',
    Component: LoadableReporting,
    pageName: 'Quick Reports',
  },
  {
    path: '/tax-returns',
    exact: true,
    Component: LoadableTaxReturns,
    pageName: 'Tax Returns List',
  },
  {
    path: '/tax-return-profile',
    exact: true,
    Component: ReturnProfile,
    pageName: 'Tax Return Profile',
  },
  {
    path: '/preparers',
    exact: true,
    Component: LoadablePreparers,
    pageName: 'Preparers',
  },
  {
    path: '/accounts',
    exact: true,
    Component: LoadableAccounts,
    pageName: 'Accounts',
  },
  {
    path: '/offices',
    exact: true,
    Component: LoadableOffices,
    pageName: 'Offices',
  },
  {
    path: '/dashboard',
    exact: true,
    Component: OverviewV2,
    pageName: 'Dashboard',
  },
  {
    path: '/calendar',
    exact: true,
    Component: LoadableAppointments,
    pageName: 'Calendar',
  },
  {
    path: '/secure-messages',
    exact: true,
    Component: LoadableMessages,
    pageName: 'Secure Messages',
  },
  {
    path: '/checks/ready-to-print',
    exact: true,
    Component: LoadableReadyToPrint,
    pageName: 'Ready to Print',
  },
  {
    path: '/server',
    exact: true,
    Component: LoadableServer,
    pageName: 'Servers',
  },
  {
    path: '/state-packages',
    exact: true,
    Component: StatePackages,
    pageName: 'State Packages',
  },
  {
    path: '/user-lookup',
    exact: true,
    Component: LoadableUserLookup,
    pageName: 'Super/Tech Support Impersonation User Lookup',
  },
  {
    path: '/tech-logins',
    exact: true,
    Component: LoadableTechLogins,
    pageName: 'Super/Tech Support User Lookup',
  },
  {
    path: '/payments',
    exact: true,
    Component: LoadablePayments,
    pageName: 'Payments',
  },
  {
    path: '/move-return',
    exact: true,
    Component: LoadableMoveReturn,
    pageName: 'Move Return',
  },
  {
    // Backward-compatible alias for existing bookmarks/deep links.
    path: '/utilities',
    exact: true,
    Component: LoadableUtilities,
    pageName: 'Utilities General',
    requiredRole: ROLE_TYPE.SUPERUSER,
  },
  {
    path: '/utilities/general',
    exact: true,
    Component: LoadableUtilities,
    pageName: 'Utilities General',
    requiredRole: ROLE_TYPE.SUPERUSER,
  },
  {
    path: '/utilities/sso',
    exact: true,
    Component: LoadableSSOMapping,
    pageName: 'Utilities SSO',
    requiredRole: ROLE_TYPE.SUPERUSER,
  },
  {
    path: '/checks/checks-register',
    Component: LoadableCheckRegister,
    pageName: 'Checks Registry',
  },
  {
    path: '/reporting/custom-reports',
    Component: LoadableReporting,
    pageName: 'Custom Reports',
  },
  {
    path: '/reporting/report-builder',
    Component: LoadableReportBuilder,
    pageName: 'Report Builder (Beta)',
    requiresPermission: true,
  },
  // Remove the navigation key once the routes below are moved to their own dedicated directory in Pages.
  {
    path: '/database',
    navigation: 'database',
    Component: AppBody,
  },
  {
    path: '*',
    Component: NotFound,
  },
];
